import { SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import useCiaData from '@components/EntityDrawers/components/CiaInputsGroup/hooks/useCiaData';
import {
  EntityReviewStatus,
  EntityValidationStatus,
  OperationalModel,
  ReadableEntityReviewStatus,
  ReadableEntityValidationStatus,
} from '@constants/canvas/layers';
import { filtersSubject } from '@context/ProjectFilters/FiltersSubject';
import { CiaFilters } from '@context/ProjectFilters/types';
import { skipToken } from '@reduxjs/toolkit/query';
import { ROUTES } from '@router/routes';
import {
  getModelFiltersState,
  ModelFilterState,
  useModelFilterActions,
  useModelFilters,
  useModelFiltersStore,
} from '@store/zustand/model-filters';
import isEqual from 'lodash.isequal';

const PROJECT_WORKSPACE_PATTERN = `${ROUTES.PROJECT.HOME}/*`;

const validationStatusOptions = [
  EntityValidationStatus.Validated,
  EntityValidationStatus.Draft,
].map((status) => ({
  label: ReadableEntityValidationStatus[status],
  value: status,
}));

const reviewStatusOptions = [EntityReviewStatus.ReviewRequired].map(
  (status) => ({
    label: ReadableEntityReviewStatus[status],
    value: status,
  }),
);

/**
 * The store fields that make up the selection `filtersSubject` carries. A
 * store write that changes none of them by value — a scope swap onto an
 * identical bucket, a clear of an already empty selection — is not a filter
 * change and must not wake the subject's subscribers.
 */
const SELECTION_KEYS: ReadonlyArray<keyof ModelFilterState> = [
  'domains',
  'validationStatus',
  'reviewStatus',
  'operationalStatuses',
  'operatingModelStatuses',
  'partners',
  'ciaFilters',
];

const isSameSelection = (next: ModelFilterState, previous: ModelFilterState) =>
  SELECTION_KEYS.every((key) => isEqual(next[key], previous[key]));

/**
 * Mirrors a selection onto `filtersSubject`, which names CIA `cia` where the
 * store names it `ciaFilters`; four canvas subscribers and `CanvasMap` read
 * that spelling.
 */
const publishSelection = (state: ModelFilterState) =>
  filtersSubject.next({
    ...filtersSubject.value,
    domains: state.domains,
    validationStatus: state.validationStatus,
    reviewStatus: state.reviewStatus,
    operationalStatuses: state.operationalStatuses,
    operatingModelStatuses: state.operatingModelStatuses,
    partners: state.partners,
    cia: state.ciaFilters,
  });

/**
 * The `ProjectFilters` context's state and setters, backed by the
 * model-filters store.
 *
 * An adapter rather than a replacement: around fifteen screens read
 * `useProjectFilters()`, while the Filters popover writes to the store
 * directly, so reading the store here gives both one source of truth without
 * touching those call sites. Every member keeps its shape; `selectedFilters`
 * stays a flat `string[]` without Scope because its readers only test
 * `.length`.
 *
 * `filtersSubject` is fed by one store subscription, so a write from the
 * popover reaches it the same way a write through these setters does. Zustand
 * runs the listener synchronously inside the write, so the subject is current
 * before React re-renders. One by-value change of the selection is one
 * emission, however many fields it touches; a write that leaves the selection
 * as it was emits nothing.
 */
export const useFilters = () => {
  const {
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    ciaFilters,
  } = useModelFilters();
  const { setCategory, setCiaFilters, clearAll } = useModelFilterActions();
  const { pathname } = useLocation();
  const workspaceProjectId = matchPath(
    { path: PROJECT_WORKSPACE_PATTERN },
    pathname,
  )?.params.id;
  const { isCiaActivated, isLoading: isProjectLoading } = useCiaData(
    workspaceProjectId ?? skipToken,
  );

  useEffect(
    () =>
      useModelFiltersStore.subscribe((state, previous) => {
        if (!isSameSelection(state, previous)) {
          publishSelection(state);
        }
      }),
    [],
  );

  const ciaSelectedFilters = useMemo(
    () =>
      Object.values(ciaFilters).reduce<string[]>((selected, fields) => {
        return [...selected, ...(Object.values(fields).flat() as string[])];
      }, []),
    [ciaFilters],
  );

  useEffect(() => {
    if (
      workspaceProjectId &&
      !isProjectLoading &&
      !isCiaActivated &&
      ciaSelectedFilters.length
    ) {
      setCiaFilters({ assurance_level: {}, coverage: {}, applicability: {} });
    }
  }, [
    workspaceProjectId,
    isCiaActivated,
    isProjectLoading,
    ciaSelectedFilters.length,
  ]);

  const selectedFilters = useMemo(
    () => [
      ...validationStatus,
      ...reviewStatus,
      ...operationalStatuses,
      ...operatingModelStatuses,
      ...ciaSelectedFilters,
      ...partners,
    ],
    [
      validationStatus,
      reviewStatus,
      operationalStatuses,
      operatingModelStatuses,
      ciaSelectedFilters,
      partners,
    ],
  );

  const handleSetValidationStatus = useCallback(
    (values: string[]) => setCategory('validationStatus', values),
    [setCategory],
  );

  const handleSetReviewStatus = useCallback(
    (values: string[]) => setCategory('reviewStatus', values),
    [setCategory],
  );

  const handleSetOperationalStatus = useCallback(
    (statuses: string[]) => setCategory('operationalStatuses', statuses),
    [setCategory],
  );

  const handleSetOperatingModelStatus = useCallback(
    (statuses: OperationalModel[]) =>
      setCategory('operatingModelStatuses', statuses),
    [setCategory],
  );

  const handleSetPartners = useCallback(
    (values: string[]) => setCategory('partners', values),
    [setCategory],
  );

  const handleSetCiaFilters = useCallback(
    (next: SetStateAction<CiaFilters>) =>
      setCiaFilters(
        typeof next === 'function'
          ? next(getModelFiltersState().ciaFilters)
          : next,
      ),
    [setCiaFilters],
  );

  return {
    validationStatusOptions,
    reviewStatusOptions,
    ciaFilters,
    validationStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    reviewStatus,
    setValidationStatus: handleSetValidationStatus,
    setReviewStatus: handleSetReviewStatus,
    setCiaFilters: handleSetCiaFilters,
    setOperationalStatuses: handleSetOperationalStatus,
    setOperatingModelStatuses: handleSetOperatingModelStatus,
    setPartners: handleSetPartners,
    selectedFilters,
    handleClearAllFilters: clearAll,
  };
};
